import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit, OnDestroy, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonService } from '../../services/common.service';
import { MandrillEmailServiceService } from '../../../customer-email/services/mandrill-email-service.service';
import { debounceTime, fromEvent, Subscription } from 'rxjs';

interface ReminderEmailLog {
  id: number;
  email: string;
  subject: string;
  status: string;
  sent_at: string;
  error_message?: string;
  messageId?: string;
}

@Component({
  selector: 'app-reminder-email-logs',
  templateUrl: './reminder-email-logs.component.html',
  styleUrls: ['./reminder-email-logs.component.scss']
})
export class ReminderEmailLogsComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('timelineContainer') timelineContainer?: ElementRef;
  @Input() searchTerm?: string;
  @Input() showFilters: boolean = true;
  @Input() autoLoad: boolean = true;
  @Input() perPage: number = 10;
  @Input() fromDate?: string;
  @Input() toDate?: string;
  @Input() customerId?: string | null; // Customer ID for filtering logs
  @Input() set forceRefresh(value: boolean) {
    if (value) {
      this.loadLogs();
      // Reset the refresh flag in the parent component
      if (this.parent) {
        this.parent.resetEmailRefresh();
      }
    }
  }
  @Input() parent?: any; // Reference to the parent LogsComponent

  @Output() dataLoaded = new EventEmitter<any>();
  @Output() error = new EventEmitter<any>();

  logs: ReminderEmailLog[] = [];
  loading: boolean = false;
  currentPage: number = 1;
  totalPages: number = 1;
  totalRecords: number = 0;

  // Modal properties
  showModal: boolean = false;
  selectedLogData: any = null;
  parsedDynamicData: Array<{field: string, value: string, isLink: boolean, displayLabel: string}> = [];

  // Preview modal properties
  showPreviewModal: boolean = false;
  previewLoading: boolean = false;
  previewEmailContent: string = '';
  previewEmailData: any = null;
  previewError: string = '';

  // Infinite scroll properties
  private scrollSubscription?: Subscription;
  public isLoadingMore: boolean = false;
  private hasMoreData: boolean = true;

  constructor(
    private commonService: CommonService,
    private changeDetectorRef: ChangeDetectorRef,
    private mandrillEmailService: MandrillEmailServiceService
  ) {}

  ngOnInit(): void {
    if (this.autoLoad) {
      // If customerId is provided, immediately load logs with it
      console.log('Initializing reminder-email-logs with customerId:', this.customerId);
      this.loadLogs();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if fromDate, toDate, searchTerm, or customerId inputs have changed
    const fromDateChanged = changes['fromDate'] && !changes['fromDate'].firstChange;
    const toDateChanged = changes['toDate'] && !changes['toDate'].firstChange;
    const searchTermChanged = changes['searchTerm'] && !changes['searchTerm'].firstChange;
    const customerIdChanged = changes['customerId'] && !changes['customerId'].firstChange;

    // If date inputs, search term, or customerId changed, reload data
    if (fromDateChanged || toDateChanged || searchTermChanged || customerIdChanged) {
      console.log('Inputs changed in reminder-email-logs, reloading data...', { 
        fromDate: this.fromDate, 
        toDate: this.toDate, 
        searchTerm: this.searchTerm,
        customerId: this.customerId 
      });
      this.loadLogs(1);
    }
  }

  ngAfterViewInit(): void {
    this.setupInfiniteScroll();
  }

  ngOnDestroy(): void {
    if (this.scrollSubscription) {
      this.scrollSubscription.unsubscribe();
    }
  }

  async loadLogs(page: number = 1): Promise<void> {
    try {
      // Reset pagination when loading new data (page 1)
      if (page === 1) {
        this.logs = [];
        this.hasMoreData = true;
        this.loading = true;
      }

      const params: any = {
        page,
        per_page: this.perPage
      };

      if (this.searchTerm) {
        params.searchTerm = this.searchTerm;
      }

      // Add customerId parameter if it exists
      if (this.customerId) {
        params.CustomerId = this.customerId;
      }

      // Add date range parameters if they exist
      if (this.fromDate) {
        params.fromDate = this.fromDate;
      }
      
      if (this.toDate) {
        params.toDate = this.toDate;
      }

      const response = await this.commonService.getReminderEmailLogs(params);
      
      if (response && response.Status) {
        // Transform the API response data to match our template structure
        const newLogs = response.data.data.map(log => ({
          id: log.id,
          email: log.Email,
          subject: log.TemplateIdentifier,
          status: log.Status,
          sent_at: log.CreatedDate,
          error_message: log.DynamicData,
          messageId: log.MessageId
        }));
        
        // If it's first page, replace data, otherwise append
        if (page === 1) {
          this.logs = newLogs;
        } else {
          this.logs = [...this.logs, ...newLogs];
        }
        
        this.currentPage = response.data.current_page;
        this.totalPages = response.data.last_page;
        this.totalRecords = response.data.total;
        this.hasMoreData = newLogs.length > 0 && response.data.current_page < response.data.last_page;
        
        this.dataLoaded.emit(response.data);
      } else {
        this.error.emit(response?.message || 'Failed to load reminder email logs');
      }
    } catch (err: any) {
      this.error.emit(err?.message || 'Error loading reminder email logs');
    } finally {
      this.loading = false;
      this.isLoadingMore = false;
    }
  }

  getStatusClass(status: string): string {
    status = status.toLowerCase();
    if (status === 'sent') {
      return 'success';
    } else if (status === 'document-pending') {
      return 'warning';
    } else if (status === 'pending documents') {
      return 'warning';
    }
    return 'secondary';
  }

  getStatusIcon(status: string): string {
    status = status.toLowerCase();
    if (status === 'sent') {
      return 'fa-check-circle';
    } else if (status === 'document-pending' || status === 'pending documents') {
      return 'fa-clock';
    }
    return 'fa-question-circle';
  }

  getDisplayStatus(status: string): string {
    status = status.toLowerCase();
    if (status === 'sent') {
      return 'Sent';
    } else if (status.includes('pending')) {
      return 'Pending';
    } else if (status === 'completed') {
      return 'Completed';
    }
    return status; // Return original status if no match
  }

  refresh(): void {
    this.loadLogs(1);
  }

  // Infinite scroll methods
  private setupInfiniteScroll(): void {
    if (!this.timelineContainer) return;

    const scrollEvent$ = fromEvent(this.timelineContainer.nativeElement, 'scroll')
      .pipe(debounceTime(150));

    this.scrollSubscription = scrollEvent$.subscribe(() => {
      this.onScroll();
    });
  }

  private onScroll(): void {
    if (!this.timelineContainer || this.isLoadingMore || !this.hasMoreData) return;

    const container = this.timelineContainer.nativeElement;
    const threshold = 200;
    
    const scrollPosition = container.scrollTop;
    const totalHeight = container.scrollHeight;
    const visibleHeight = container.clientHeight;
    
    const distanceFromBottom = totalHeight - (scrollPosition + visibleHeight);

    if (distanceFromBottom < threshold) {
      this.loadMore();
    }
  }

  private async loadMore(): Promise<void> {
    if (this.isLoadingMore || !this.hasMoreData) return;
    
    this.isLoadingMore = true;
    
    try {
      const nextPage = this.currentPage + 1;
      await this.loadLogs(nextPage);
    } catch (err) {
      this.error.emit(err);
      this.hasMoreData = false;
    }
  }

  // Modal methods
  openModal(log: ReminderEmailLog): void {
    this.selectedLogData = log;
    this.parsedDynamicData = this.parseDynamicData(log.error_message);
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.selectedLogData = null;
    this.parsedDynamicData = [];
  }

  formatDynamicData(data: any): string {
    if (!data) return 'No additional data available';
    
    try {
      // If it's already a string, return it
      if (typeof data === 'string') {
        // Try to parse it as JSON first
        try {
          const parsed = JSON.parse(data);
          return JSON.stringify(parsed, null, 2);
        } catch {
          return data;
        }
      }
      // If it's an object, stringify it with formatting
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }

  // New method to parse dynamic data into structured format
  parseDynamicData(data: any): Array<{field: string, value: string, isLink: boolean, displayLabel: string}> {
    if (!data) return [];
    
    // Handle empty string case
    if (typeof data === 'string' && data.trim() === '') {
      return [];
    }
    
    try {
      let parsedData: any;
      
      // Parse the JSON string
      if (typeof data === 'string') {
        parsedData = JSON.parse(data);
      } else {
        parsedData = data;
      }

      // Field mapping for better display labels
      const fieldMapping: {[key: string]: string} = {
        'FNAME': 'First Name',
        'LNAME': 'Last Name', 
        'ENROLMENT_CODE': 'Enrollment Code',
        'SAVE_FOR_LATER': 'Save for Later',
        'DUE_DATE': 'Due Date',
        'RESUME_LINK': 'Resume Link',
        'START_DATE': 'Start Date',
        'SENT_TO_EMAIL': 'Sent to Email',
        'PENDING_DOCUMENT_LIST': 'Pending Documents',
        'UPLOAD_LINK': 'Upload Link',
        'CUSTOMER_ID': 'Customer ID',
        'ORDER_ID': 'Order ID',
        'PLAN_NAME': 'Plan Name',
        'PHONE_NUMBER': 'Phone Number',
        'EMAIL': 'Email Address'
      };

      const result: Array<{field: string, value: string, isLink: boolean, displayLabel: string}> = [];

      Object.keys(parsedData).forEach(key => {
        const value = parsedData[key];
        const displayLabel = fieldMapping[key] || this.formatFieldName(key);
        
        // Check if value is a URL
        const isLink = this.isValidUrl(value);
        
        // Clean up HTML tags for display
        let cleanValue = value;
        if (typeof value === 'string' && value.includes('<')) {
          cleanValue = value.replace(/<[^>]*>/g, '').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
        }

        result.push({
          field: key,
          value: cleanValue,
          isLink: isLink,
          displayLabel: displayLabel
        });
      });

      return result;
    } catch (error) {
      return [];
    }
  }

  // Helper method to format field names
  private formatFieldName(fieldName: string): string {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Helper method to check if a string is a valid URL
  private isValidUrl(string: string): boolean {
    try {
      const url = new URL(string);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }

  // Method to open links in new tab
  openLink(url: string): void {
    window.open(url, '_blank');
  }

  // Preview button click handler
  onPreviewClick(messageId: string): void {
    if (!messageId) {
      this.commonService.showToast('error', 'Error', 'Message ID is required');
      return;
    }

    console.log('Preview clicked for messageId:', messageId);
    
    // Reset modal state
    this.previewLoading = true;
    this.previewEmailContent = '';
    this.previewEmailData = null;
    this.previewError = '';
    this.showPreviewModal = true;

    // Call the API
    const payload = {
      emailId: messageId
    };

    console.log('Calling getMandrillEmailPreview API with payload:', payload);

    this.mandrillEmailService.getMandrillEmailPreview(payload).subscribe({
      next: (response) => {
        console.log('Mandrill API response:', response);
        this.previewLoading = false;
        
        // Check different possible response structures
        if (response) {
          let emailData = null;
          
          // Case 1: Response has Status property and data
          if (response.Status && response.data) {
            emailData = response.data;
            console.log('Using response.data:', emailData);
          }
          // Case 2: Response itself is the email data (direct response)
          else if (response.html || response.subject || response.from_email) {
            emailData = response;
            console.log('Using response directly as email data:', emailData);
          }
          // Case 3: Response has a different success indicator
          else if (response.status === 'success' || response.success) {
            emailData = response.data || response;
            console.log('Using success response data:', emailData);
          }
          // Case 4: Try to use response as-is if it has email-like properties
          else if (response.to || response.email || response._id) {
            emailData = response;
            console.log('Using response as email data based on properties:', emailData);
          }
          
          if (emailData) {
            console.log('Processing email data:', emailData);
            console.log('To field:', emailData.to);
            console.log('Tags field:', emailData.tags);
            console.log('Subject field:', emailData.subject);
            console.log('Text field:', emailData.text);
            
            // Store the complete email data for metadata display
            this.previewEmailData = emailData;
            
            // Extract HTML content from the email data
            this.previewEmailContent = emailData.html || emailData.content || '';
            console.log('HTML content found:', !!this.previewEmailContent);
            
            console.log('Email data processed successfully');
          } else {
            console.log('No valid email data found in response');
            this.previewError = 'No email data available in response';
          }
        } else {
          console.log('No response received');
          this.previewError = 'No response received from server';
        }
      },
      error: (error) => {
        console.error('Error fetching Mandrill email preview:', error);
        this.previewLoading = false;
        this.previewError = 'Failed to load email preview. Please try again.';
      }
    });
  }

  closePreviewModal(): void {
    this.showPreviewModal = false;
    this.previewEmailContent = '';
    this.previewEmailData = null;
    this.previewError = '';
  }

  convertTimestamp(timestamp: number): Date {
    return new Date(timestamp * 1000);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatToField(toData: any): string {
    if (!toData) return '';
    
    // If it's already a string, return it
    if (typeof toData === 'string') {
      return toData;
    }
    
    // If it's an array of objects (like in your API response)
    if (Array.isArray(toData)) {
      return toData.map(recipient => {
        if (typeof recipient === 'string') {
          return recipient;
        }
        // Handle object format like {email: "email@example.com", name: "Name"}
        if (recipient.email) {
          return recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email;
        }
        // If it's just an object with other properties, try to extract email
        return recipient.toString();
      }).join(', ');
    }
    
    // If it's a single object
    if (typeof toData === 'object') {
      if (toData.email) {
        return toData.name ? `${toData.name} <${toData.email}>` : toData.email;
      }
    }
    
    // Fallback to string conversion
    return toData.toString();
  }

  formatTagsArray(tags: any): string[] {
    if (!tags) return [];
    
    // If it's already an array of strings
    if (Array.isArray(tags)) {
      return tags.map(tag => typeof tag === 'string' ? tag : tag.toString());
    }
    
    // If it's a single value
    if (typeof tags === 'string') {
      return [tags];
    }
    
    // If it's an object, try to extract meaningful values
    if (typeof tags === 'object') {
      return [tags.toString()];
    }
    
    return [];
  }

  private generateEmailMetadataPreview(emailData: any): string {
    const metadata = [];
    
    if (emailData.subject) {
      metadata.push(`<h4>Subject: ${emailData.subject}</h4>`);
    }
    
    if (emailData.from_email) {
      const fromName = emailData.from_name ? `${emailData.from_name} <${emailData.from_email}>` : emailData.from_email;
      metadata.push(`<p><strong>From:</strong> ${fromName}</p>`);
    }
    
    if (emailData.to && Array.isArray(emailData.to)) {
      const toList = emailData.to.map((recipient: any) => {
        return recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email;
      }).join(', ');
      metadata.push(`<p><strong>To:</strong> ${toList}</p>`);
    } else if (emailData.email) {
      const toName = emailData.name ? `${emailData.name} <${emailData.email}>` : emailData.email;
      metadata.push(`<p><strong>To:</strong> ${toName}</p>`);
    }
    
    if (emailData.ts) {
      const date = new Date(emailData.ts * 1000).toLocaleString();
      metadata.push(`<p><strong>Date:</strong> ${date}</p>`);
    }
    
    if (emailData.tags && emailData.tags.length > 0) {
      metadata.push(`<p><strong>Tags:</strong> ${emailData.tags.join(', ')}</p>`);
    }
    
    if (emailData.text) {
      metadata.push(`<hr><h5>Text Content:</h5><pre style="white-space: pre-wrap; background: #f8f9fa; padding: 15px; border-radius: 4px;">${emailData.text}</pre>`);
    }
    
    if (emailData.attachments && emailData.attachments.length > 0) {
      metadata.push(`<hr><h5>Attachments:</h5><ul>${emailData.attachments.map((att: any) => `<li>${att.name || 'Attachment'}</li>`).join('')}</ul>`);
    }
    
    return `
      <div style="font-family: Arial, sans-serif; line-height: 1.6;">
        ${metadata.join('')}
        <hr>
        <p><em>No HTML content available for this email.</em></p>
      </div>
    `;
  }
} 